import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { bidAnalysisServices, MarketLandscapeItem } from 'services/bidAnalysis/bidAnalysisServices';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import classes from '../../bidAnalysisTemplate.module.scss';
import { SectionHeader } from '../shared/SectionHeader';
import { MarketLandscapeTable } from '../tables/MarketLandscapeTable';

interface MarketLandscapeSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  bidId: number;
  productName: string;
  notesCount?: number;
  onNotesClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDataChange?: (data: MarketLandscapeItem[]) => void;
  externalData?: MarketLandscapeItem[];
}

export const MarketLandscapeSection: React.FC<MarketLandscapeSectionProps> = ({
  isExpanded,
  onToggle,
  bidId,
  productName,
  notesCount: _notesCount = 0,
  onNotesClick: _onNotesClick,
  onDataChange,
  externalData,
}) => {
  // Sort market landscape data by compliance status:
  // 1. Red (is_compliant = false) - first
  // 2. Green (is_compliant = true) - second
  // 3. Normal (is_compliant = null) - third
  const sortByCompliance = (data: MarketLandscapeItem[]): MarketLandscapeItem[] => {
    return [...data].sort((a, b) => {
      // Get sort priority: false = 0, true = 1, null = 2
      const getPriority = (isCompliant: boolean | null): number => {
        if (isCompliant === false) return 0; // Red - first
        if (isCompliant === true) return 1; // Green - second
        return 2; // null - third
      };

      return getPriority(a.is_compliant) - getPriority(b.is_compliant);
    });
  };

  const [marketLandscapeData, setMarketLandscapeData] = useState<MarketLandscapeItem[]>(
    sortByCompliance(externalData || [])
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [addRowModalVisible, setAddRowModalVisible] = useState(false);
  const [newRowData, setNewRowData] = useState<MarketLandscapeItem>({
    supplier: '',
    api: '',
    mfg_co: '',
    note: '',
    is_compliant: null,
  });
  const originalDataRef = useRef<MarketLandscapeItem[]>([]); // Store original data when entering edit mode
  const hasLoadedDataRef = useRef<number | null>(null); // Track which bidId has been loaded

  // Sync with external data changes and ensure sorting
  // Only update if externalData actually changed (not just reference)
  const prevExternalDataRef = useRef<MarketLandscapeItem[] | undefined>(externalData);
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    // Skip if this is an internal update (to prevent loops)
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // Only update if externalData actually changed
    if (externalData && externalData.length > 0 && externalData !== prevExternalDataRef.current) {
      // Simple check: only update if length changed or it's a new reference
      const shouldUpdate =
        !prevExternalDataRef.current ||
        prevExternalDataRef.current.length !== externalData.length ||
        prevExternalDataRef.current !== externalData;

      if (shouldUpdate) {
        const sortedExternalData = sortByCompliance(externalData);
        setMarketLandscapeData(sortedExternalData);
        prevExternalDataRef.current = externalData;
      }
    }
  }, [externalData]);

  // Load market landscape data on page load - always call market-landscape endpoint first
  // Only load once per bidId, not when isExpanded changes or Edit is clicked
  useEffect(() => {
    if (bidId && isExpanded && hasLoadedDataRef.current !== bidId) {
      loadMarketLandscapeData();
      hasLoadedDataRef.current = bidId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId, isExpanded]); // Depend on both, but use ref to prevent reload on Edit

  const loadMarketLandscapeData = async () => {
    setIsLoading(true);
    try {
      // Always call /bid/market-landscape/<bid-id> first on page load
      const response = await bidAnalysisServices.getMarketLandscape(bidId);

      // If data is empty, call labellers API to fill the table
      if (response.success && response.data.length === 0 && productName) {
        try {
          const labellersResponse =
            await bidAnalysisServices.getMarketLandscapeLabellers(productName);
          if (labellersResponse.success && labellersResponse.data.length > 0) {
            const labellersData = labellersResponse.data;
            // Sort data before storing
            const sortedLabellersData = sortByCompliance(labellersData);
            setMarketLandscapeData(sortedLabellersData);
            originalDataRef.current = [...sortedLabellersData]; // Store as original data
            // Update parent state if callback is provided
            if (onDataChange) {
              onDataChange(sortedLabellersData);
            }
            return;
          }
        } catch (labellersError) {
          console.error('Error loading labellers data:', labellersError);
          // Continue with empty data if labellers API fails
        }
      }

      // Set data from market-landscape endpoint (even if empty)
      const marketLandscapeData = response.data || [];
      // Sort data before storing
      const sortedData = sortByCompliance(marketLandscapeData);
      setMarketLandscapeData(sortedData);
      originalDataRef.current = [...sortedData]; // Store as original data
      // Update parent state if callback is provided
      if (onDataChange) {
        onDataChange(sortedData);
      }
    } catch (error) {
      console.error('Error loading market landscape:', error);
      toast.error('Failed to load market landscape data', { autoClose: 3000 });
      setMarketLandscapeData([]);
      if (onDataChange) {
        onDataChange([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent accordion from closing
    // Ensure data is sorted before entering edit mode, then store as original
    const sortedData = sortByCompliance(marketLandscapeData);
    setMarketLandscapeData(sortedData);
    originalDataRef.current = [...sortedData];
    // Don't call onDataChange here - it would trigger externalData update and cause infinite loop
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    // Restore original data without API call (already sorted)
    const restoredData = [...originalDataRef.current];
    setMarketLandscapeData(restoredData);
    if (onDataChange) {
      onDataChange(restoredData);
    }
    setIsEditMode(false);
  };

  const handleSaveChanges = async (dataToSave: MarketLandscapeItem[]) => {
    setIsSaving(true);
    try {
      // Sort data before saving
      const sortedData = sortByCompliance(dataToSave);
      await bidAnalysisServices.updateMarketLandscape(bidId, {
        market_landscape: sortedData,
      });
      toast.success('Market landscape saved successfully!', { autoClose: 3000 });
      // Update state and original data ref with sorted data
      isInternalUpdateRef.current = true; // Mark as internal update to prevent loop
      setMarketLandscapeData(sortedData);
      originalDataRef.current = [...sortedData];
      if (onDataChange) {
        onDataChange(sortedData);
      }
      setIsEditMode(false);
    } catch (error: any) {
      console.error('Error saving market landscape:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to save market landscape';
      toast.error(`Failed to save: ${errorMessage}`, { autoClose: 5000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRow = () => {
    // Reset form and open modal
    setNewRowData({
      supplier: '',
      api: '',
      mfg_co: '',
      note: '',
      is_compliant: null,
    });
    setAddRowModalVisible(true);
  };

  const handleAddRowModalConfirm = () => {
    // Add the new row to the data and sort immediately (since we're adding a new row)
    const updatedData = sortByCompliance([...marketLandscapeData, { ...newRowData }]);
    setMarketLandscapeData(updatedData);
    originalDataRef.current = [...updatedData]; // Update original data ref
    if (onDataChange) {
      onDataChange(updatedData);
    }
    setAddRowModalVisible(false);
    // Reset form
    setNewRowData({
      supplier: '',
      api: '',
      mfg_co: '',
      note: '',
      is_compliant: null,
    });
  };

  const handleAddRowModalCancel = () => {
    setAddRowModalVisible(false);
    // Reset form
    setNewRowData({
      supplier: '',
      api: '',
      mfg_co: '',
      note: '',
      is_compliant: null,
    });
  };

  const handleDeleteRow = (index: number) => {
    // Data is already sorted in state, so index matches directly
    setRowToDelete(index);
    setDeleteConfirmationVisible(true);
  };

  const confirmDeleteRow = () => {
    if (rowToDelete !== null) {
      // After delete, data is already in correct order (just one item removed)
      // No need to re-sort as the order is maintained
      const updatedData = marketLandscapeData.filter((_, i) => i !== rowToDelete);
      setMarketLandscapeData(updatedData);
      originalDataRef.current = [...updatedData]; // Update original data ref
      if (onDataChange) {
        onDataChange(updatedData);
      }
      setRowToDelete(null);
    }
    setDeleteConfirmationVisible(false);
  };

  const cancelDeleteRow = () => {
    setRowToDelete(null);
    setDeleteConfirmationVisible(false);
  };

  const handleDataChange = useCallback((data: MarketLandscapeItem[]) => {
    // During editing, don't sort on every keystroke - just update state
    // Sorting will happen on save or when exiting edit mode
    // Don't call onDataChange during editing to prevent infinite loops
    setMarketLandscapeData(data);
  }, []);

  return (
    <div className={classes.section}>
      <SectionHeader
        title="Market Landscape"
        isExpanded={isExpanded}
        onToggle={onToggle}
        // Don't show Review Notes button for Market Landscape
      >
        {isExpanded && !isEditMode && (
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={handleEditClick}
            className="p-button-outlined"
            size="small"
            style={{ marginRight: '0.5rem' }}
          />
        )}
      </SectionHeader>

      {isExpanded && (
        <div className={classes.sectionContent}>
          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#522d6e' }}>
              <i
                className="pi pi-spinner pi-spin"
                style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
              ></i>
              <p>Loading market landscape data...</p>
            </div>
          ) : (
            <>
              <MarketLandscapeTable
                data={marketLandscapeData}
                isEditMode={isEditMode}
                onDataChange={handleDataChange}
                onDeleteRow={handleDeleteRow}
                onAddRow={handleAddRow}
                onSave={tableData => {
                  // Use the data directly from the table (includes all user edits)
                  setMarketLandscapeData(tableData);
                  handleSaveChanges(tableData);
                }}
                onCancel={handleCancelEdit}
                isSaving={isSaving}
              />
            </>
          )}
        </div>
      )}

      <ConfirmationModal
        visibility={deleteConfirmationVisible}
        modalHeading="Confirm Delete"
        modalContent="Are you sure you want to delete this row?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        handleConfirm={confirmDeleteRow}
        handleCancel={cancelDeleteRow}
        confirmButtonStyle="purple"
      />

      {/* Add Row Modal */}
      <Dialog
        visible={addRowModalVisible}
        onHide={handleAddRowModalCancel}
        header="Add New Row"
        modal
        style={{ width: '500px' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={handleAddRowModalCancel}
              className="p-button-outlined"
            />
            <Button
              label="Add"
              icon="pi pi-check"
              onClick={handleAddRowModalConfirm}
              style={{ backgroundColor: '#4d2e6d', borderColor: '#4d2e6d', color: 'white' }}
            />
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
          <div>
            <label
              htmlFor="supplier"
              style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
            >
              Supplier *
            </label>
            <InputText
              id="supplier"
              value={newRowData.supplier}
              onChange={e => setNewRowData({ ...newRowData, supplier: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Enter supplier name"
            />
          </div>
          <div>
            <label
              htmlFor="api"
              style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
            >
              API
            </label>
            <InputText
              id="api"
              value={newRowData.api}
              onChange={e => setNewRowData({ ...newRowData, api: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Enter API"
            />
          </div>
          <div>
            <label
              htmlFor="mfg_co"
              style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
            >
              MFG Co
            </label>
            <InputText
              id="mfg_co"
              value={newRowData.mfg_co}
              onChange={e => setNewRowData({ ...newRowData, mfg_co: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Enter manufacturing company"
            />
          </div>
          <div>
            <label
              htmlFor="note"
              style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
            >
              Notes
            </label>
            <InputText
              id="note"
              value={newRowData.note}
              onChange={e => setNewRowData({ ...newRowData, note: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Enter notes"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Compliant
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setNewRowData({ ...newRowData, is_compliant: null })}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  border:
                    newRowData.is_compliant === null ? '2px solid #4d2e6d' : '2px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                }}
                title="Not Set"
              />
              <button
                type="button"
                onClick={() => setNewRowData({ ...newRowData, is_compliant: true })}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  border:
                    newRowData.is_compliant === true ? '2px solid #4d2e6d' : '2px solid #d1d5db',
                  backgroundColor: '#d1fae5',
                  cursor: 'pointer',
                }}
                title="Compliant"
              />
              <button
                type="button"
                onClick={() => setNewRowData({ ...newRowData, is_compliant: false })}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  border:
                    newRowData.is_compliant === false ? '2px solid #4d2e6d' : '2px solid #d1d5db',
                  backgroundColor: '#fee2e2',
                  cursor: 'pointer',
                }}
                title="Not Compliant"
              />
              <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                {newRowData.is_compliant === null && 'Not Set'}
                {newRowData.is_compliant === true && 'Compliant'}
                {newRowData.is_compliant === false && 'Not Compliant'}
              </span>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
